/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * Hero Component
 *
 * --- REWRITE SUMMARY (V13 - Remove Floating Particles) ---
 *
 * MISSION: To optimize the hero section performance by removing the heavy
 * floating particles animation.
 *
 * THE FIX (Removal):
 * 1. Removed `HeroBackground` import and usage.
 * 2. Removed `showBackground` state and lazy loading logic for the background.
 *
 * RESULT: The hero section now loads significantly faster, displaying the
 * optimized image and then the video, without the resource-intensive particles.
 */
import { useTranslation, Trans } from 'react-i18next';
import { FaVideo } from 'react-icons/fa';
import { useNavContext } from '@/context/NavigationProvider';
import { useState, useEffect, useRef } from 'react';

import heroVideo from '@/assets/vids/hero-background.mp4';

const Hero = () => {
  const { t } = useTranslation();
  const { scrollToSection } = useNavContext();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoSrc, setVideoSrc] = useState('');

  // --- PERFORMANCE: Lazy load the video ---
  useEffect(() => {
    // Small delay to ensure the main thread is free and the image is visible
    const videoTimer = setTimeout(() => {
      setVideoSrc(heroVideo);
    }, 100);
    return () => clearTimeout(videoTimer);
  }, []);

  const videoRef = useRef(null);

  // Check readyState in case events were missed
  useEffect(() => {
    if (videoRef.current && videoRef.current.readyState >= 3) {
      setVideoLoaded(true);
    }
  }, [videoSrc]);

  return (
    <section id="hero-section" className="hero">
      {/* 
        --- PERFORMANCE: Static Image Layer (LCP Optimized) ---
        Displayed immediately. Uses responsive images for mobile/desktop. 
      */}
      <div className="absolute inset-0 z-0">
        <picture>
          <source media="(min-width: 1440px)" srcSet="/assets/img/hero-bg-desktop.webp" />
          <source media="(min-width: 768px)" srcSet="/assets/img/hero-bg-tablet.webp" />
          <img
            src="/assets/img/hero-bg-mobile.webp"
            alt="Hero Background"
            className="w-full h-full object-cover"
            height="1920"
            // eslint-disable-next-line react/no-unknown-property
            fetchpriority="high" // Signal to browser this is important
          />
        </picture>
      </div>

      {/* 
        --- PERFORMANCE: Lazy Loaded Video ---
        Appears instantly when ready (no fade). 
      */}
      {/* 
        --- PERFORMANCE: Lazy Loaded Video ---
        Appears instantly when ready (no fade). 
      */}
      {videoSrc && (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          className={`absolute inset-0 w-full h-full object-cover z-0 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
          src={videoSrc}
          onCanPlay={() => setVideoLoaded(true)}
          onLoadedData={() => setVideoLoaded(true)}
          onError={(e) => console.error("Video load error:", e)}
        >
          Your browser does not support the video tag.
        </video>
      )}

      {/* Content Overlay */}
      <div className="container hero-content px-8 md:px-16 xl:px-24 relative z-10">
        <div className="w-full lg:max-w-3xl xl:max-w-4xl 2xl:max-w-[66%]">
          <h1 className="text-3xl md:text-5xl font-montserrat font-bold mb-6 text-white leading-tight">
            <Trans i18nKey="hero.title" components={[<span className="pink" key="0" />]} />
          </h1>

          <p className="text-lg text-gray-200 mb-8">
            {t('hero.subtitle1')}
          </p>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => scrollToSection('filmmaker-cta', { block: 'center' })}
              className="btn btn-primary rounded-full gap-2"
            >
              <FaVideo />
              {t('hero.becomeFilmmaker')}
            </button>

            {/* The litepaper button has been completely removed to fix linting errors */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
